import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { HelixBookRepositoryService } from '../../services/helix-book-repository.service';
import {
  Observable,
  Subject,
  filter,
  first,
  map,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ListSection } from '../../interfaces/list-section.interface';
import { KbItemListResponse } from '@medindex-webapi';
import { PlatformService } from '@shared/services/platform.service';

@Component({
  selector: 'app-helix-book-list',
  templateUrl: './list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListComponent implements AfterViewInit, OnDestroy {
  @ViewChildren('section') sectionElements: QueryList<ElementRef> | undefined;

  constructor(
    private platformService: PlatformService,
    private repository: HelixBookRepositoryService
  ) {}

  ngUnsubscribe$ = new Subject<void>();
  observer: IntersectionObserver | null = null;

  ngAfterViewInit(): void {
    this.repository.scrollEvent$
      .pipe(
        filter((x) => x && this.platformService.isBrowser),
        switchMap(() => this.repository.activeKey$.pipe(first())),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((key) => {
        const element = document.getElementById(`kb-section-${key}`);
        element?.scrollIntoView({
          block: 'start',
          inline: 'nearest',
        });
        this.repository.setScrollEvent(false);
      });
    this._initViewObserver();
  }

  sections$: Observable<ListSection[]> = this.repository.getItemsList().pipe(
    map((list) => {
      const allSymbols = list
        .map((x) => (x.title ? x.title[0].toUpperCase() : ''))
        .filter((value, index, arr) => !!value && arr.indexOf(value) === index);

      return allSymbols.map((symbol) => {
        return {
          symbol: symbol,
          items: list.filter((x) => x.title?.toUpperCase()?.startsWith(symbol)),
        };
      });
    })
  );

  sectionTrackBy(index: number, section: ListSection) {
    return section.symbol;
  }

  itemTrackBy(index: number, item: KbItemListResponse) {
    return item.id;
  }

  private _initViewObserver(): void {
    if (this.platformService.isServer) return;

    const options: IntersectionObserverInit = {
      threshold: 0.1,
      rootMargin: '0px 0px -90%',
    };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const key = entry.target.innerHTML.trim();
          this.repository.setActiveKey(key);
        }
      });
    }, options);

    this.sectionElements?.forEach((el) =>
      this.observer?.observe(el.nativeElement)
    );
    this.sectionElements?.changes
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((els: QueryList<ElementRef>) => {
        els.forEach((el) => this.observer?.observe(el.nativeElement));
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.observer?.disconnect();
  }
}
